import type { CSSProperties, ReactNode } from 'react';

export type TableCell = {
  content?: string;
  isBold?: boolean;
  badge?: boolean;
  badgeColor?: string;
  icon?: 'tick' | 'cross' | 'dash' | 'none' | string;
  link?: string;
};

export type TableRow = {
  cells?: TableCell[];
};

export type TableBlockValue = {
  caption?: string;
  style?: string;
  size?: string;
  alignment?: string;
  highlightColor?: string;
  highlightedColumnIndex?: number;
  hasHeaderRow?: boolean;
  hasHeaderColumn?: boolean;
  hasFooterRow?: boolean;
  isFullWidth?: boolean;
  rows?: TableRow[];
  footnote?: string;
  sourceText?: string;
  sourceUrl?: string;
};

type TableBlockProps = {
  value: TableBlockValue;
  variant?: 'blog' | 'use-case';
  highlightedColumnIndex?: number;
};

const ICONS: Record<string, ReactNode> = {
  tick: <span className="cell-icon tick">✓</span>,
  cross: <span className="cell-icon cross">✗</span>,
  dash: <span className="cell-icon dash">—</span>,
};

const HEADER_COLORS: Record<string, { bg: string; text: string }> = {
  gray: {
    bg: 'var(--color-surface-container-high)',
    text: 'var(--color-on-surface)',
  },
  blue: {
    bg: 'rgba(59, 130, 246, 0.15)',
    text: 'rgb(147, 197, 253)',
  },
  green: {
    bg: 'rgba(34, 197, 94, 0.15)',
    text: 'rgb(134, 239, 172)',
  },
  red: {
    bg: 'rgba(239, 68, 68, 0.15)',
    text: 'rgb(252, 165, 165)',
  },
  purple: {
    bg: 'rgba(168, 85, 247, 0.15)',
    text: 'rgb(216, 180, 254)',
  },
  orange: {
    bg: 'rgba(249, 115, 22, 0.15)',
    text: 'rgb(253, 186, 116)',
  },
};

const SIZE_PADDING: Record<string, string> = {
  compact: '6px 10px',
  normal: '10px 14px',
  relaxed: '14px 18px',
};

function CellContent({ cell }: { cell: TableCell }) {
  const icon = cell.icon && cell.icon !== 'none' ? ICONS[cell.icon] ?? null : null;

  let content: ReactNode = (
    <span style={{ fontWeight: cell.isBold ? 700 : 'inherit' }}>
      {icon}
      {cell.content}
    </span>
  );

  if (cell.badge && cell.content) {
    content = (
      <span className={`cell-badge badge-${cell.badgeColor || 'gray'}`}>
        {icon}
        {cell.content}
      </span>
    );
  }

  if (cell.link) {
    content = (
      <a href={cell.link} target="_blank" rel="noopener noreferrer" className="cell-link">
        {content}
      </a>
    );
  }

  return content;
}

function highlightClass(
  variant: 'blog' | 'use-case',
  cellIndex: number,
  highlightIndex: number | undefined,
) {
  if (variant !== 'use-case' || highlightIndex === undefined) return '';
  return cellIndex === highlightIndex ? 'table-col-highlight' : '';
}

export function TableBlock({
  value,
  variant = 'blog',
  highlightedColumnIndex,
}: TableBlockProps) {
  const {
    rows = [],
    style = 'default',
    size = 'normal',
    alignment = 'left',
    highlightColor = 'gray',
    hasHeaderRow = true,
    hasHeaderColumn = false,
    hasFooterRow = false,
    isFullWidth = true,
    caption,
    footnote,
    sourceText,
    sourceUrl,
  } = value;

  const highlightIndex =
    highlightedColumnIndex ?? value.highlightedColumnIndex ?? (variant === 'use-case' ? 1 : undefined);

  const headerColors = HEADER_COLORS[highlightColor] ?? HEADER_COLORS.gray;
  const padding = SIZE_PADDING[size] ?? SIZE_PADDING.normal;
  const textAlign = alignment as CSSProperties['textAlign'];

  const headerRow = hasHeaderRow ? rows[0] : null;
  const footerRow = hasFooterRow ? rows[rows.length - 1] : null;
  const bodyRows = rows.slice(
    hasHeaderRow ? 1 : 0,
    hasFooterRow ? rows.length - 1 : rows.length,
  );

  const headerStyle: CSSProperties = {
    padding,
    textAlign,
    background: headerColors.bg,
    color: headerColors.text,
    fontWeight: 500,
  };

  const cellStyle: CSSProperties = {
    padding,
    textAlign,
  };

  const renderCell = (
    cell: TableCell,
    cellIndex: number,
    asHeader: boolean,
    rowKey: string,
  ) => {
    const isColHeader = hasHeaderColumn && cellIndex === 0;
    const Tag = asHeader || isColHeader ? 'th' : 'td';
    const hiClass = highlightClass(variant, cellIndex, highlightIndex);

    return (
      <Tag
        key={`${rowKey}-${cellIndex}`}
        style={asHeader || isColHeader ? headerStyle : cellStyle}
        className={`${asHeader || isColHeader ? 'table-header-cell' : 'table-cell'}${hiClass ? ` ${hiClass}` : ''}`}
      >
        <CellContent cell={cell} />
      </Tag>
    );
  };

  const tableClass =
    variant === 'use-case' ? `use-case-table table-${style} table-${size}` : `blog-table table-${style} table-${size}`;

  return (
    <div
      className={`table-wrapper my-8 table-style-${style} ${isFullWidth ? 'full-width' : ''}${variant === 'use-case' ? ' table-wrapper--use-case' : ''}`}
    >
      <div className="table-scroll">
        <table className={tableClass}>
          {caption && <caption className="table-caption-top">{caption}</caption>}

          {headerRow && (
            <thead>
              <tr>
                {headerRow.cells?.map((cell, i) => renderCell(cell, i, true, 'head'))}
              </tr>
            </thead>
          )}

          <tbody>
            {bodyRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.cells?.map((cell, cellIndex) =>
                  renderCell(cell, cellIndex, false, `body-${rowIndex}`),
                )}
              </tr>
            ))}
          </tbody>

          {footerRow && (
            <tfoot>
              <tr>
                {footerRow.cells?.map((cell, i) => renderCell(cell, i, true, 'foot'))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {footnote && <p className="table-footnote">* {footnote}</p>}
      {sourceText && (
        <p className="table-source">
          Source:{' '}
          {sourceUrl ? (
            <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
              {sourceText}
            </a>
          ) : (
            sourceText
          )}
        </p>
      )}
    </div>
  );
}
